
import { supabase } from '@/integrations/supabase/client';

export interface Subscription {
  id: string;
  user_id: string;
  email: string;
  stripe_customer_id?: string;
  subscribed: boolean;
  subscription_tier: 'free' | 'pro' | 'expert';
  subscription_end?: string;
  created_at: string;
  updated_at: string;
}

export const subscriptionService = {
  // Get current user's subscription
  async getCurrentSubscription(): Promise<Subscription | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('subscribers')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching subscription:', error);
      return null;
    }

    if (!data) return null;

    // Cast the database response to our Subscription type
    return {
      ...data,
      subscription_tier: (data.subscription_tier as 'free' | 'pro' | 'expert') || 'free'
    } as Subscription;
  },

  // Create or update subscription record
  async upsertSubscription(subscriptionData: Partial<Subscription>): Promise<Subscription | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('subscribers')
      .upsert({
        user_id: user.id,
        email: user.email!,
        ...subscriptionData
      })
      .select()
      .single();

    if (error) {
      console.error('Error upserting subscription:', error);
      throw error;
    }

    // Cast the database response to our Subscription type
    return {
      ...data,
      subscription_tier: (data.subscription_tier as 'free' | 'pro' | 'expert') || 'free'
    } as Subscription;
  },

  // Upgrade to pro (temporary - will be replaced with Stripe integration)
  async upgradeToPro(): Promise<Subscription | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // For now, just upgrade directly. Later this will handle Stripe checkout
    const subscriptionEnd = new Date();
    subscriptionEnd.setMonth(subscriptionEnd.getMonth() + 1); // 1 month from now

    return this.upsertSubscription({
      subscribed: true,
      subscription_tier: 'pro',
      subscription_end: subscriptionEnd.toISOString()
    });
  },

  // Check if user has active pro subscription
  async hasActivePro(): Promise<boolean> {
    const subscription = await this.getCurrentSubscription();
    
    if (!subscription || !subscription.subscribed || (subscription.subscription_tier !== 'pro' && subscription.subscription_tier !== 'expert')) {
      return false;
    }

    // Check if subscription is still active
    if (subscription.subscription_end) {
      const endDate = new Date(subscription.subscription_end);
      const now = new Date();
      return endDate > now;
    }

    return true;
  },

  // Upgrade to expert (temporary - will be replaced with Stripe integration)
  async upgradeToExpert(): Promise<Subscription | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // For now, just upgrade directly. Later this will handle Stripe checkout
    const subscriptionEnd = new Date();
    subscriptionEnd.setMonth(subscriptionEnd.getMonth() + 1); // 1 month from now

    return this.upsertSubscription({
      subscribed: true,
      subscription_tier: 'expert',
      subscription_end: subscriptionEnd.toISOString()
    });
  },

  // Initialize free subscription for new users
  async initializeFreeSubscription(): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Check if subscription already exists
    const existing = await this.getCurrentSubscription();
    if (existing) return;

    // Create free subscription
    await this.upsertSubscription({
      subscribed: false,
      subscription_tier: 'free'
    });
  }
};
